'use client';

import { useState } from 'react';
import { signIn } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useLogger } from '@lib/utils/logUtils';

export default function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginAttempted, setLoginAttempted] = useState(false);
  const [clientInfo, setClientInfo] = useState({
    isMobile: false,
    isIOS: false,
    origin: '',
    isReady: false
  });
  
  const logger = useLogger('LoginForm');
  
  // Effect to detect device info and handle URL error parameters
  useState(() => {
    // Only run in browser
    if (typeof window === 'undefined') return;
    
    setClientInfo({
      isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
      isIOS: /iPhone|iPad|iPod/i.test(navigator.userAgent),
      origin: window.location.origin,
      isReady: true
    });
    
    // Handle URL error parameters
    const errorParam = searchParams.get('error');
    if (errorParam === 'session_expired') {
      setError('Your session has expired. Please sign in again.');
      // Log session expiry
      logAuthEvent('session-expired', {
        timestamp: new Date().toISOString(),
        referrer: document.referrer
      }).catch(e => logger.error('Failed to log session expiry:', e));
    }
    
    // Show iOS warning if needed
    if (/iPhone|iPad|iPod/i.test(navigator.userAgent) && !loginAttempted) {
      setError('For iOS users: If login fails, try disabling "Prevent Cross-Site Tracking" in Safari settings, or use Chrome/Firefox.');
    }
  });

  const handleGoogleSignIn = async () => {
    if (!clientInfo.isReady) {
      logger.warn('Login attempted before client info was initialized');
      setError('Page is still loading, please wait a moment and try again.');
      return;
    }
    
    try {
      setIsLoading(true);
      setError('');
      setLoginAttempted(true);
      
      // Generate tracking ID with more robust method
      const authState = generateAuthState();
      logger.info(`Starting auth flow with tracking ID: ${authState}`);
      
      // Create callback URL for login redirect
      const callbackUrl = new URL('/', clientInfo.origin).toString();
      
      // Log attempt with client-safe values
      await logAuthEvent('auth-attempt', {
        trackingId: authState,
        isIOS: clientInfo.isIOS,
        isMobile: clientInfo.isMobile,
        windowOrigin: clientInfo.origin,
        timestamp: new Date().toISOString()
      });
      
      try {
        // Try the next-auth signin with Google
        const result = await signIn('google', { 
          callbackUrl,
          redirect: false // Use false to handle redirect manually for better error handling
        });
        
        // Check if signin was successful
        if (result) {
          if (result.ok) {
            // Success case - redirect to callback URL or result URL
            router.push(result.url || callbackUrl);
          } else {
            // Handle error from next-auth
            logger.warn(`SignIn request failed with status ${result.status || 'unknown'}. Trying direct route...`);
            
            // Log the failure to monitor
            await logAuthEvent('signin-error', {
              trackingId: authState,
              status: result.status,
              error: result.error
            });
            
            // Try direct auth route as fallback
            await redirectToDirectAuth(callbackUrl);
          }
        }
      } catch (signInError) {
        // If the normal flow fails (e.g., due to RSC-related issues), use our direct route
        const errorMessage = signInError instanceof Error ? signInError.message : 'Unknown error';
        logger.warn(`SignIn failed with error: ${errorMessage}. Trying direct route...`);
        
        // Log the exception to monitor
        await logAuthEvent('signin-exception', {
          trackingId: authState,
          error: errorMessage
        });
        
        // Use direct auth route as fallback
        await redirectToDirectAuth(callbackUrl);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      logger.error('Sign in error:', errorMessage);
      
      // Log the fatal error to monitor
      await logAuthEvent('signin-fatal-error', {
        error: errorMessage
      });
      
      // Update error state
      setError('An error occurred during sign in. Please try again.');
      setIsLoading(false);
    }
  };
  
  // Redirect to our custom direct auth endpoint
  const redirectToDirectAuth = async (callbackUrl: string) => {
    try {
      // Complete all state updates before navigation
      setIsLoading(true);
      
      const csrfToken = await fetchCsrfToken();
      const redirectUrl = `/api/auth/direct-signin?provider=google&callbackUrl=${encodeURIComponent(
        callbackUrl
      )}&csrfToken=${encodeURIComponent(csrfToken)}`;
      
      // Use router for client-side navigation when possible
      if (typeof window !== 'undefined') {
        // Use a timeout to ensure state updates complete before navigation
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 0);
      }
    } catch (error) {
      logger.error('Error during redirect:', error instanceof Error ? error.message : 'Unknown error');
      setError('Failed to redirect to authentication. Please try again.');
      setIsLoading(false);
    }
  };
  
  // Fetch CSRF token for authentication
  const fetchCsrfToken = async (): Promise<string> => {
    try {
      const response = await fetch('/api/auth/csrf');
      const data = await response.json();
      return data.csrfToken || '';
    } catch (error) {
      logger.error('Error fetching CSRF token:', error instanceof Error ? error.message : 'Unknown error');
      return '';
    }
  };
  
  // Generate a random state parameter for CSRF protection
  const generateAuthState = () => {
    return Array.from(crypto.getRandomValues(new Uint8Array(8)))
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');
  };
  
  // Log auth-related events to our monitor endpoint
  const logAuthEvent = async (event: string, details: any) => {
    try {
      // Avoid AbortSignal.timeout which may not be supported in all browsers
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 1000);
      
      await fetch('/api/auth/monitor', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          event,
          details
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
    } catch (error) {
      // Silent fail - don't block auth flow for logging errors
      console.error('Failed to log auth event', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 py-6 sm:py-12">
      <div className="w-full max-w-md space-y-6 p-5 sm:p-8 bg-white rounded-lg shadow-md">
        {!clientInfo.isReady ? (
          <div className="py-6 text-center">
            <div className="animate-pulse h-6 w-3/4 mx-auto bg-gray-200 rounded"></div>
            <div className="mt-4 animate-pulse h-10 w-full bg-gray-200 rounded"></div>
          </div>
        ) : (
          <>
            <div>
              <h2 className="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-900">
                Sign in to your account
              </h2>
              {error && (
                <div className="mt-2 text-center text-sm text-red-600 p-2 border border-red-100 bg-red-50 rounded">
                  {error}
                </div>
              )}
              {loginAttempted && !error && isLoading ? (
                <p className="mt-2 text-center text-sm text-blue-600">
                  Redirecting to Google login...
                </p>
              ) : !loginAttempted && (
                <p className="mt-2 text-center text-sm text-gray-600">
                  After clicking Sign In, you&apos;ll be redirected to Google authentication.
                </p>
              )}
            </div>
            <div className="mt-6 sm:mt-8 space-y-4 sm:space-y-6">
              <button
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                className={`group relative w-full flex justify-center py-2 sm:py-3 px-4 border border-transparent text-sm sm:text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all ${
                  isLoading ? 'opacity-70 cursor-not-allowed' : ''
                }`}
                aria-label="Sign in with Google"
              >
                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                  <svg className="h-5 w-5 text-blue-500 group-hover:text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
                  </svg>
                </span>
                <span className="pl-2">{isLoading ? 'Signing in...' : 'Sign in with Google'}</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
} 